import { useState } from "react";
import type { IntakeData, IntakeItem } from "../../api/documents";

interface Props {
  initial?: IntakeData | null;
  onCancel: () => void;
  onSubmit: (data: IntakeData) => Promise<void> | void;
}

const inputSt: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8,
  background: "#1a2030", color: "#f9fafb", border: "1px solid #374151", fontSize: 13,
};
const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, minWidth: 0 };
const cap: React.CSSProperties = { fontSize: 11, color: "#6b7280" };

const DEVICE_TYPES = ["Ноутбук", "ПК (системный блок)", "Моноблок", "Монитор", "Принтер / МФУ", "Планшет", "Телефон", "Другое"];
const emptyItem = (): IntakeItem => ({ type: "Ноутбук", model: "", serial: "", accessories: "", condition: "", complaint: "" });

/** Акт приёма техники: несколько устройств, вид работ, предварительная стоимость и срок. Этикетка создаётся автоматически. */
export default function IntakePanel({ initial, onCancel, onSubmit }: Props) {
  const [items, setItems] = useState<IntakeItem[]>(initial?.items?.length ? initial.items : [emptyItem()]);
  const [workType, setWorkType] = useState(initial?.workType ?? "Диагностика");
  const [estimate, setEstimate] = useState(initial?.estimate ? String(initial.estimate) : "");
  const [readyDate, setReadyDate] = useState(initial?.readyDate ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [busy, setBusy] = useState(false);

  const setItem = (i: number, patch: Partial<IntakeItem>) =>
    setItems((list) => list.map((it, k) => (k === i ? { ...it, ...patch } : it)));

  const submit = async () => {
    const filled = items.filter((it) => (it.model || it.serial || it.complaint || "").trim());
    if (!filled.length) { alert("Укажите хотя бы одно устройство: модель, серийный номер или неисправность"); return; }
    setBusy(true);
    try {
      await onSubmit({ items: filled, workType, estimate: Number(estimate) > 0 ? Number(estimate) : null, readyDate: readyDate || null, note });
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Не удалось создать акт приёма");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 14, borderRadius: 10, background: "#131926", border: "1px solid #1f2937", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#e5e7eb" }}>Приём техники</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
        Будут созданы акт приёма с квитанцией и этикетка 60×40 мм (по одной на каждое устройство).
      </div>

      {items.map((it, i) => (
        <div key={i} style={{ border: "1px solid #1f2937", borderRadius: 10, padding: 10, marginBottom: 10, display: "grid", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>Устройство {i + 1}</span>
            {items.length > 1 && (
              <button type="button" onClick={() => setItems((l) => l.filter((_, k) => k !== i))}
                style={{ background: "none", border: "1px solid #374151", color: "#f87171", borderRadius: 6, cursor: "pointer", padding: "0 8px" }}>✕</button>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
            <label style={lbl}><span style={cap}>Тип</span>
              <select style={inputSt} value={it.type} onChange={(e) => setItem(i, { type: e.target.value })}>
                {DEVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label style={lbl}><span style={cap}>Модель</span>
              <input style={inputSt} value={it.model ?? ""} onChange={(e) => setItem(i, { model: e.target.value })} placeholder="ASUS X515" />
            </label>
            <label style={lbl}><span style={cap}>Серийный №</span>
              <input style={inputSt} value={it.serial ?? ""} onChange={(e) => setItem(i, { serial: e.target.value })} />
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
            <label style={lbl}><span style={cap}>Комплектность</span>
              <input style={inputSt} value={it.accessories ?? ""} onChange={(e) => setItem(i, { accessories: e.target.value })} placeholder="зарядное, сумка" />
            </label>
            <label style={lbl}><span style={cap}>Внешний вид / состояние</span>
              <input style={inputSt} value={it.condition ?? ""} onChange={(e) => setItem(i, { condition: e.target.value })} placeholder="царапины на крышке" />
            </label>
          </div>
          <label style={lbl}><span style={cap}>Заявленная неисправность</span>
            <textarea style={{ ...inputSt, minHeight: 56, resize: "vertical", fontFamily: "inherit" }} value={it.complaint ?? ""} onChange={(e) => setItem(i, { complaint: e.target.value })} />
          </label>
        </div>
      ))}

      {items.length < 12 && (
        <button type="button" onClick={() => setItems((l) => [...l, emptyItem()])}
          style={{ background: "transparent", border: "1px dashed #374151", color: "#9ca3af", borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontSize: 13, marginBottom: 12 }}>
          + Ещё устройство
        </button>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginBottom: 8 }}>
        <label style={lbl}><span style={cap}>Вид работ</span>
          <input style={inputSt} value={workType} onChange={(e) => setWorkType(e.target.value)} />
        </label>
        <label style={lbl}><span style={cap}>Предв. стоимость, ₸</span>
          <input style={inputSt} type="number" inputMode="decimal" value={estimate} onChange={(e) => setEstimate(e.target.value)} placeholder="не указана" />
        </label>
        <label style={lbl}><span style={cap}>Срок готовности</span>
          <input style={inputSt} type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} />
        </label>
      </div>
      <label style={{ ...lbl, marginBottom: 12 }}><span style={cap}>Примечание</span>
        <input style={inputSt} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button type="button" onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #374151", background: "transparent", color: "#9ca3af", cursor: "pointer", fontSize: 13 }}>Отмена</button>
        <button type="button" disabled={busy} onClick={submit}
          style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#10b981", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, opacity: busy ? 0.6 : 1 }}>
          {busy ? "Создаю…" : "Принять и создать акт + этикетку"}
        </button>
      </div>
    </div>
  );
}
